// Google Analytics 4 (gtag.js) integration
// Consent-gated: only loads when user accepts analytics cookies

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Extend globalThis to include gtag globals
declare global {
  // eslint-disable-next-line no-var
  var dataLayer: unknown[];
  // eslint-disable-next-line no-var
  var gtag: (...args: unknown[]) => void;
}

let initialized = false;

const SCRIPT_ID = "ga-gtag-script";

/**
 * Initialize GA4 by injecting the gtag.js script.
 * Called only when the user grants analytics consent.
 * Safe to call multiple times - subsequent calls are no-ops.
 */
export function initGA(): void {
  if (!GA_MEASUREMENT_ID) return;

  // Clear any previous GA opt-out flag so tracking resumes after re-consent
  (globalThis as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

  if (initialized) return;

  // Inject the gtag.js loader script only once (survives disable/re-enable cycles)
  if (!document.getElementById(SCRIPT_ID)) {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.async = true;
    document.head.appendChild(script);
  }

  // Initialize the dataLayer and gtag function
  globalThis.dataLayer = globalThis.dataLayer || [];
  globalThis.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    globalThis.dataLayer.push(arguments);
  };
  globalThis.gtag("js", new Date());
  globalThis.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false, // We fire page views manually on route changes
  });

  initialized = true;
}

/**
 * Disable GA4 and remove cookies when user revokes analytics consent.
 */
export function disableGA(): void {
  if (!GA_MEASUREMENT_ID) return;

  // Set the GA opt-out flag
  (globalThis as unknown as Record<string, unknown>)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;

  // Remove GA cookies across host-only, dotted hostname, and registrable domain
  const cookiesToRemove = ["_ga", `_ga_${GA_MEASUREMENT_ID.replace("G-", "")}`, "_gid"];
  const hostname = globalThis.location.hostname;
  const parts = hostname.split(".");
  const domains = [`.${hostname}`, ""]; // dotted hostname + host-only
  if (parts.length > 2) {
    domains.push(`.${parts.slice(-2).join(".")}`); // registrable domain (e.g. .example.com)
  }
  for (const name of cookiesToRemove) {
    for (const domain of domains) {
      const domainAttr = domain ? `; domain=${domain}` : "";
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${domainAttr}`;
    }
  }

  initialized = false;
}

/**
 * Track a page view. Called on every route change.
 */
export function trackPageView(path: string, title?: string): void {
  if (!initialized || !GA_MEASUREMENT_ID) return;
  globalThis.gtag("config", GA_MEASUREMENT_ID, {
    page_path: path,
    page_title: title ?? document.title,
  });
}

/**
 * Track a custom event (e.g. sign_up, contact_form_submit).
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
): void {
  if (!initialized || !GA_MEASUREMENT_ID) return;
  globalThis.gtag("event", eventName, params);
}
